import { defineStore } from 'pinia'
import { resolveAccount } from '@/services/accountService'

/**
 * Shared-domain hostnames where the accountSlug must appear in the URL.
 * On whitelabel domains the slug is inferred from the domain itself.
 */
const SHARED_DOMAIN_HOSTS = ['booking.gopgms.com', 'localhost', '127.0.0.1']

/**
 * Account Store — caches the resolved account (domain → account mapping).
 *
 * Called once at app startup. The resolved account_id, branches list,
 * and accountSlug are available to all services and components.
 */
export const useAccountStore = defineStore('account', {
  state: () => ({
    accountId: null,
    accountSlug: null,
    branches: [],
    accountData: null,
    isResolved: false,
    loading: false,
    error: null
  }),

  getters: {
    /** The first branch ID (convenience for single-property whitelabel sites). */
    primaryBranchId: (state) => state.branches?.[0]?.id ?? null,

    /** Whether the resolved account has multiple branches (marketplace mode). */
    isMarketplace: (state) => (state.branches?.length ?? 0) > 1,

    /** Convenience alias for the account slug. */
    slug: (state) => state.accountSlug,

    /**
     * Whether the app is running on a shared/marketplace domain.
     * On shared domains the accountSlug is required in the URL path.
     */
    isSharedDomain: () => {
      const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
      return SHARED_DOMAIN_HOSTS.includes(hostname)
    }
  },

  actions: {
    /**
     * Resolve the current domain to an account.
     * Safe to call multiple times — subsequent calls are no-ops once resolved.
     *
     * @param {string} [domain] — Override domain. Defaults to window.location.hostname.
     * @param {string} [slug]   — Account slug from the URL (shared domain).
     */
    async resolve(domain, slug) {
      if (this.isResolved || this.loading) {
        return this.accountData
      }

      const resolvedDomain = domain || (typeof window !== 'undefined' ? window.location.hostname : '')

      this.loading = true
      this.error = null

      try {
        const data = await resolveAccount(resolvedDomain, slug)

        this.accountId = data.account_id ?? data.accountId ?? null
        this.accountSlug = data.accountSlug ?? data.account_slug ?? data.slug ?? null
        this.branches = Array.isArray(data.branches) ? data.branches : []
        this.accountData = data
        this.isResolved = true

        return data
      } catch (err) {
        const status = err?.response?.status
        const message = err?.response?.data?.data || err?.message || 'Failed to resolve account'
        this.error = { status, message }

        // Don't block the app — mark as resolved even on failure
        // so other API calls proceed using X-Domain-Name header fallback
        this.isResolved = true
        return null
      } finally {
        this.loading = false
      }
    }
  }
})
